import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsis } from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import { gql } from '@soundxyz/gql-string';
import { styled } from '@components-shared/src/stitches';
import { AudienceSeating } from '@components/Song/Audience/Earliest';
import { TopAudience } from '@components/Song/Audience/Top';
import {
  AudienceContainer,
  AudienceHeader,
  Container,
  TabsSection,
} from '@components/Song/Audience/shared/AudienceHeader';
import { EmptySeatingModal } from '@components/Song/Audience/shared/EmptySeatingModal';
import { GlobalDropdown } from '@components/basics/Dropdowns/GlobalDropdown';
import { ExportCSVMenuItem } from '@components/basics/Dropdowns/MenuItems/ExportCSVMenuItem';
import { IconButton } from '@components/main-components/icon-buttons/IconButton';
import { List, TabContent, Tabs, Trigger } from '@components/main-components/tabs/Tab';
import { DisplayToggle } from '@components/shared';
import { ToastContext } from '@context/toast';
import type { FragmentType } from '@graphql/reactQuery';
import {
  AudienceSongInfoFragmentDoc,
  fetchQuery,
  getFragment,
  ReleaseBackersCsvUrlDocument,
} from '@graphql/reactQuery';
import { useSubscribeRelease } from '@hooks/useSubscribeRelease';
import { Fade } from '../shared';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment AudienceSongInfo on Release {
    id
    numSold
    ...EmptySeatingModalInfo
    ...AudienceSeatingSongInfo
    ...ReleaseCommentType
    ...ReleaseUpdates
  }

  query releaseBackersCSVUrl($releaseId: String!) {
    releaseBackersCSVUrl(releaseId: $releaseId)
  }
`);

export const OldAudienceSection = ({
  releaseFrag,
}: {
  releaseFrag: FragmentType<AudienceSongInfoFragmentDoc>;
}) => {
  const release = getFragment(AudienceSongInfoFragmentDoc, releaseFrag);
  const { openToast } = ToastContext.useContainer();

  const downloadCSV = async () => {
    try {
      const {
        data: { releaseBackersCSVUrl },
      } = await fetchQuery(ReleaseBackersCsvUrlDocument, {
        variables: {
          releaseId: release.id,
        },
      });
      if (releaseBackersCSVUrl) {
        window.open(releaseBackersCSVUrl);
      } else {
        throw Error('Could not find release backers CSV file');
      }
    } catch (e) {
      openToast({
        text: 'Download CSV release backers failed.',
        variant: 'error',
      });
    }
  };

  const { data: subscriptionData } = useSubscribeRelease({
    release,
  });

  const hasAudience = subscriptionData.totalMinted > 0;

  const container = React.useRef<HTMLDivElement>(null);
  return (
    <AudienceWrapper>
      <Tabs defaultValue="top">
        <AudienceHeader>
          {hasAudience && (
            <TabsSection>
              <StyledTabList>
                <StyledTrigger value="top">Top</StyledTrigger>
                <StyledTrigger value="earliest">Earliest</StyledTrigger>
              </StyledTabList>

              {/* For now do not show export CSV on mobile devices */}
              <DisplayToggle media="tablet">
                <GlobalDropdown
                  align="end"
                  css={{ width: 'auto' }}
                  trigger={<IconButton icon={<FontAwesomeIcon icon={faEllipsis} />} />}
                >
                  <ExportCSVMenuItem onClick={downloadCSV} />
                </GlobalDropdown>
              </DisplayToggle>
            </TabsSection>
          )}
        </AudienceHeader>

        <AudienceContainer ref={container}>
          <TabContent value="top">
            <TopAudience releaseId={release.id} />
          </TabContent>
          <TabContent value="earliest">
            <AudienceSeating releaseFrag={release} />
          </TabContent>
          {!hasAudience && (
            <>
              <Fade
                endOpacity={0.8}
                css={{
                  display: 'flex',
                  width: '100%',
                  height: '100%',
                  position: 'absolute',
                  left: 0,

                  '@tablet': {
                    left: 'unset',
                    maxWidth: 'unset',
                  },
                }}
              />
              <EmptyModal>
                <EmptySeatingModal release={release} />
              </EmptyModal>
            </>
          )}
        </AudienceContainer>
      </Tabs>
    </AudienceWrapper>
  );
};

export const AudienceWrapper = styled(Container, {
  overflow: 'visible',
  marginTop: 32,
  '@tablet': {
    marginTop: 64,
  },
});

const StyledTabList = styled(List, {
  display: 'flex',
  flexShrink: 0,
  gap: 8,
});

const StyledTrigger = styled(Trigger, {
  display: 'flex',
  alignItems: 'center',
  base: 'M',
  height: 32,
  color: '$base800',
  backgroundColor: '$neutral100',
  padding: '10px 12px',
  borderRadius: 6,

  '&:hover': {
    backgroundColor: '$neutral300',
  },

  '&[data-state="active"]': {
    backgroundColor: '$base800',
    color: '$white',
  },

  '&[data-disabled]': {
    cursor: 'not-allowed',
    backgroundColor: '$neutral100',
    color: '$neutral400',
  },
});

const EmptyModal = styled('div', {
  position: 'absolute',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  height: '100%',
});
